import { useEffect, useState } from "react";

import type { AdminListPagination, AdminProductSummary } from "@chaochud/contracts";

import { AuditTrail, CriticalActionForm, DataTable, PageHeader, StatusPill, toAuditTrailEntry } from "@/components/admin-ui";
import { adminOpsClient } from "@/lib/api-clients";
import type { AuditTrailEntry } from "@/components/admin-ui";

const STATUS_TONE: Record<string, "success" | "warning" | "error" | "neutral"> = {
  ACTIVE: "success",
  DRAFT: "neutral",
  SUSPENDED_BY_ADMIN: "error",
};

export function ProductsPage() {
  const [items, setItems] = useState<AdminProductSummary[]>([]);
  const [pagination, setPagination] = useState<AdminListPagination | null>(null);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<AdminProductSummary | null>(null);
  const [auditEntries, setAuditEntries] = useState<AuditTrailEntry[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);

  const load = async (targetPage = page) => {
    const data = await adminOpsClient.listProducts(targetPage);
    setItems(data.items);
    setPagination(data.pagination);
  };

  useEffect(() => {
    void load(page);
  }, [page]);

  useEffect(() => {
    if (!selected) {
      setAuditEntries([]);
      return;
    }
    setAuditLoading(true);
    adminOpsClient
      .listResourceAuditLogs("PRODUCT", selected.id)
      .then((data) => setAuditEntries(data.items.map(toAuditTrailEntry)))
      .catch(() => setAuditEntries([]))
      .finally(() => setAuditLoading(false));
  }, [selected]);

  return (
    <div className="space-y-6">
      <PageHeader title="ตรวจสอบสินค้า" />
      <DataTable
        headers={["ชื่อ", "ร้าน", "สถานะ", ""]}
        rows={items.map((product) => [
          product.name,
          product.storeName,
          <StatusPill key={`status-${product.id}`} tone={STATUS_TONE[product.status] ?? "neutral"}>
            {product.status}
          </StatusPill>,
          <button
            key={product.id}
            type="button"
            className="text-sm font-semibold text-plum hover:underline"
            onClick={() => setSelected(product)}
          >
            จัดการ
          </button>,
        ])}
        pagination={pagination ?? undefined}
        onPageChange={setPage}
      />
      {selected ? (
        <div className="grid gap-4 md:grid-cols-[1fr_280px]">
          {selected.status === "SUSPENDED_BY_ADMIN" ? (
            <CriticalActionForm
              title="คืนสถานะสินค้า"
              targetLabel={selected.name}
              currentState={selected.status}
              expectedState="ACTIVE"
              impact="สินค้าจะกลับไปแสดงใน marketplace ได้ถ้าเงื่อนไขอื่นผ่าน"
              submitLabel="Restore"
              onSubmit={async (payload) => {
                await adminOpsClient.restoreProduct(selected.id, payload);
                setSelected(null);
                await load();
              }}
            />
          ) : (
            <CriticalActionForm
              title="ระงับสินค้าโดยแอดมิน"
              targetLabel={selected.name}
              currentState={selected.status}
              expectedState="SUSPENDED_BY_ADMIN"
              impact="สินค้าจะถูกซ่อนจากการค้นหา"
              submitLabel="Suspend product"
              onSubmit={async (payload) => {
                await adminOpsClient.suspendProduct(selected.id, payload);
                setSelected(null);
                await load();
              }}
            />
          )}
          <AuditTrail entries={auditEntries} isLoading={auditLoading} />
        </div>
      ) : null}
    </div>
  );
}
